import { z } from "zod";

const consultationStatusSchema = z.enum([
  "recording",
  "uploading",
  "transcribing",
  "normalizing",
  "drafting",
  "ready_for_review",
  "validated",
  "failed",
]);

const consultationCorrectionSchema = z.object({
  before: z.string(),
  after: z.string(),
  reason: z.string(),
  requiresReview: z.boolean(),
});

const consultationSchema = z.object({
  id: z.string().uuid(),
  status: consultationStatusSchema,
  recordingStartedAt: z.string(),
  recordingEndedAt: z.string().nullable(),
  audioExpiresAt: z.string().nullable(),
  rawTranscript: z.string().nullable(),
  normalizedTranscript: z.string().nullable(),
  corrections: z.array(consultationCorrectionSchema),
  draftReport: z.string().nullable(),
  validatedReport: z.string().nullable(),
  validatedAt: z.string().nullable(),
  errorMessage: z.string().nullable(),
});

const audioUrlSchema = z.object({
  signedUrl: z.string().url(),
  expiresIn: z.number().int().positive(),
});

export type Consultation = z.infer<typeof consultationSchema>;

const consultationsApiBaseUrl = "/cadendarConsultations";

async function request(path: string, options?: RequestInit) {
  const response = await fetch(`${consultationsApiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });
  if (!response.ok) {
    if (response.status === 401) {
      window.location.reload();
    }
    const body = await response.json().catch(() => null);
    const message =
      typeof body === "object" && body !== null && "error" in body && typeof body.error === "string"
        ? body.error
        : "La requête de consultation a échoué.";
    throw new Error(message);
  }
  return response.json();
}

function patientPath(patientId: string) {
  return `/patients/${encodeURIComponent(patientId)}/consultations`;
}

function consultationPath(consultationId: string) {
  return `/consultations/${encodeURIComponent(consultationId)}`;
}

export const consultationsApi = {
  async list(patientId: string) {
    return z.array(consultationSchema).parse(await request(patientPath(patientId)));
  },
  async start(options: { patientId: string; audioMimeType: string }) {
    return consultationSchema.parse(
      await request(patientPath(options.patientId), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ audioMimeType: options.audioMimeType }),
      }),
    );
  },
  async uploadChunk(options: { consultationId: string; sequenceNumber: number; chunk: Blob }) {
    const formData = new FormData();
    formData.append("sequenceNumber", String(options.sequenceNumber));
    formData.append("audio", options.chunk, `consultation-${options.sequenceNumber}.webm`);
    await request(`${consultationPath(options.consultationId)}/chunks`, {
      method: "POST",
      body: formData,
    });
  },
  async finish(consultationId: string) {
    await request(`${consultationPath(consultationId)}/finish`, { method: "POST" });
  },
  async updateDraft(options: { consultationId: string; draftReport: string }) {
    return consultationSchema.parse(
      await request(`${consultationPath(options.consultationId)}/draft`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ draftReport: options.draftReport }),
      }),
    );
  },
  async validate(consultationId: string) {
    return consultationSchema.parse(
      await request(`${consultationPath(consultationId)}/validate`, { method: "POST" }),
    );
  },
  async retry(consultationId: string) {
    await request(`${consultationPath(consultationId)}/retry`, { method: "POST" });
  },
  async getAudioUrl(consultationId: string) {
    return audioUrlSchema.parse(await request(`${consultationPath(consultationId)}/audio-url`));
  },
};
